import lodashCloneDeep from 'lodash/cloneDeep';
import lodashGet from 'lodash/get';
import lodashSet from 'lodash/set';
import lodashHas from 'lodash/has';
import lodashMerge from 'lodash/merge';
import appConfig from '../app.config.json';
import { LATEST_VERSION } from '../constant.js';
import { appendTrailingSlash } from '../utils.js';
import { SecurityContext } from '../typings/common.js';

/**
 * service configuration
 */
interface ServiceEndpoints {
  /**
   * api endpoint url
   */
  api?: string;
  /**
   * idp endpoint url
   */
  idp?: string;
  /**
   * logger endpoint url
   */
  logger: string;
}

interface Env {
  serviceEndpoints: ServiceEndpoints;
}

/**
 * Files specific to the mode of the micro-frontend application.
 */
type EnvConfig = {
  /**
   * Array of file paths specific to the environment (development or production).
   * relative or absolute URLs
   */
  files: Array<string>;
};

/**
 * Modes in which a micro-frontend application can operate.
 */
export enum LaunchMode {
  DEVELOPMENT = 'development',
  PRODUCTION = 'production',
}

/**
 * Library that manages the micro-frontend application.
 */
export enum MicroAppManager {
  SSF = 'ssf',
  APPSDK = 'appsdk',
}

/**
 * guest micro frontend app configuration
 */
export type MicroFrontendApp = {
  /**
   * unique application id
   */
  id: string;
  /**
   * application name
   */
  name: string;
  /**
   * Optional title for the micro-frontend application.
   */
  title?: string;
  /**
   * Mode in which the micro-frontend operates (e.g., 'development', 'production').
   */
  mode?: LaunchMode;
  /**
   * url where the application is hosted
   */
  hostUrl: string;
  /**
   * location of the manifest file
   */
  manifestPath?: string;
  /**
   * home route of the application
   */
  homeRoute?: string;
  /**
   * If true, the micro-frontend is treated as a JavaScript module.
   */
  isJsModule?: boolean;
  /**
   * Library that manages the micro-frontend application (e.g., 'ssf', 'appsdk').
   * @default 'appsdk'
   */
  microappManager?: MicroAppManager;
  /**
   * security context under which the guest application is running
   */
  securityContext?: SecurityContext;
  /**
   * static files to be loaded in dev mode
   */
  development: EnvConfig;
  /**
   * static files to be loaded in prod mode
   */
  production: EnvConfig;
};

/**
 * host frontend application configuration
 */
export interface IAppConfig {
  /**
   * unique application id
   */
  appId: string;
  /**
   * brand name
   */
  brand?: string;
  /**
   * current environment
   */
  activeEnv: string;
  /**
   * time interval to warn user about session timeout. milliseconds
   */
  sessionTimeoutWarnInterval?: string | number;
  /**
   * time interval to logout user after session timeout. milliseconds
   */
  sessionTimeoutInterval?: string | number;
  /**
   * service configuration
   */
  serviceEndpoints: ServiceEndpoints;
  /**
   * environment specific configs
   */
  env: { [key: string]: Env };
  /**
   * micro frontend application configs
   */
  microFrontendApps: Record<string, Omit<MicroFrontendApp, 'id'>>;
}

/**
 * Utility to manage application configuration
 */
export class CAppConfig {
  #gAppConfig: IAppConfig = appConfig as unknown as IAppConfig;

  #version: string;

  #baseUrl: string;

  constructor(params: { version?: string; baseUrl?: string }) {
    this.#version = params?.version || LATEST_VERSION;
    this.#baseUrl = appendTrailingSlash(params?.baseUrl || '');
  }

  #parse = (data: IAppConfig) => {
    const { activeEnv, env, ...rest } = data;
    const activeEnvConfig = env[activeEnv] || {};
    this.#gAppConfig = lodashMerge({}, rest, activeEnvConfig) as IAppConfig;
  };

  /**
   * Get value for the given app config key
   * @param key key to get value for
   * @param defaultValue default value to return if key is not found
   * @returns value for the given key
   */
  get = <Type>(key = '', defaultValue: unknown = null): Type =>
    lodashCloneDeep(lodashGet(this.#gAppConfig, key, defaultValue)) as Type;

  /**
   * Set value for the given app config key
   * @param key key to set value for
   * @param value value to set
   */
  set = <Type>(key: string, value: Type): void => {
    lodashSet(this.#gAppConfig, key, value);
  };

  /**
   * Check if the given key exists in app config
   * @param key key to check
   * @returns true if key exists
   */
  has = (key = '') => lodashHas(this.#gAppConfig, key);

  /**
   * Accept an already-parsed config object, skipping the HTTP fetch.
   * Useful when the caller (e.g. pui-app-sdk) has already loaded the config.
   * @param config
   */
  setPreloadedConfig = (config: Record<string, unknown>) => {
    this.#gAppConfig = config as unknown as IAppConfig;
  };

  /**
   * add version to the base url
   * @returns versioned base url
   */
  #getVersionedBaseUrl = () => {
    // Match trailing "/X.Y" or "/latest" (with optional trailing slash) to detect if version is already present
    const regex = /(?:\/)(\d+\.\d+|latest)(?:\/?)?$/;
    if (!regex.test(this.#baseUrl)) {
      const majorMinorVersion = this.#version.match(/^(?:\d+\.\d+)*/g);
      return appendTrailingSlash(
        `${this.#baseUrl}${majorMinorVersion?.[0] || LATEST_VERSION}`,
      );
    }
    return this.#baseUrl;
  };

  /**
   * Resolves the unversioned "latest" base URL for app.config.json fallback.
   * Replaces a trailing major.minor or latest segment instead of appending latest
   * onto an already-versioned base (which would produce paths like 26.3/latest/).
   */
  #getLatestBaseUrl = (): string => {
    const versionedBase = this.#getVersionedBaseUrl();
    return appendTrailingSlash(
      versionedBase.replace(/(\/)(\d+\.\d+|latest)\/?$/, '$1latest/'),
    );
  };

  /**
   * load application configuration from the given asset path
   * @param assetPath url path to load app config from
   * @param configUrl
   */
  // eslint-disable-next-line complexity
  load = async (configUrl?: string) => {
    const appConfigUrl =
      configUrl ?? `${this.#getVersionedBaseUrl()}app.config.json`;
    const latestConfigUrl = `${this.#getLatestBaseUrl()}app.config.json`;
    const shouldFallbackToLatest = (requestedUrl: string) =>
      !configUrl && requestedUrl !== latestConfigUrl;
    let response: Response;
    try {
      response = await fetch(appConfigUrl);
    } catch (err) {
      if (shouldFallbackToLatest(appConfigUrl)) {
        await this.load(latestConfigUrl);
        return;
      }
      throw new Error('Failed to fetch app config', {
        cause: err as Error,
      });
    }
    if (response.ok) {
      try {
        const data = (await response.json()) as IAppConfig;
        this.#parse(data);
      } catch (err) {
        if (shouldFallbackToLatest(appConfigUrl)) {
          await this.load(latestConfigUrl);
          return;
        }
        throw new Error('Failed to parse app config', {
          cause: err as Error,
        });
      }
    } else if (shouldFallbackToLatest(appConfigUrl)) {
      await this.load(latestConfigUrl);
    } else {
      throw new Error('Failed to load app config');
    }
  };
}
